import React, {useEffect, useMemo, useState} from 'react';
import {ModelDTO} from 'hemwb-api';
import {Spin} from 'antd';
import {Form, useForm} from '../../../core/Form';
import {FormControl, FormGroup} from 'react-reactive-form';
import {ModelCreator, ModelFilterState, ModelStewardInfo} from './types';
import {extractDistinctMetadata, mapOptions} from './utils';
import {MultipleCheckboxFilter} from '../../../core/filter';

const compareStewards = (a: ModelStewardInfo, b: ModelStewardInfo) => {
  const nameCmp = a.name.localeCompare(b.name);
  if (nameCmp === 0) {
    return a.email.localeCompare(b.email);
  }
  return nameCmp;
};

const buildStewardKey = (steward: ModelStewardInfo) => steward.name + ';' + steward.email;

const buildCreatorKey = (creator: ModelCreator) => creator.id;

const compareCreators = (a: ModelCreator, b: ModelCreator) => {
  return a.id.localeCompare(b.id);
};

export type ModelFilterProps = {
  models: ModelDTO[] | null;
  filter: ModelFilterState | null;
  onFilterChange: (filter: ModelFilterState) => void;
  isHorizontal?: boolean;
};

export const ModelFilter: React.FC<ModelFilterProps> = ({models, filter, onFilterChange, isHorizontal}) => {
  const options = useMemo(() => {
    const countries = extractDistinctMetadata(models, 'country');

    const globalOptions = [];
    if (models) {
      if (models.find((model) => model.metadata.global === 'true')) {
        globalOptions.push('global');
      }
      if (models.find((model) => !!model.metadata.country)) {
        globalOptions.push('countryAdaptation');
      }
    }
    return {
      modelTypes: extractDistinctMetadata(models, 'model_type'),
      global: globalOptions,
      countries: countries,
      stewards: Object.values(
        (models || [])
          .map((model) =>
            model.metadata && model.metadata.name_of_model_steward
              ? {
                  name: model.metadata.name_of_model_steward,
                  email: model.metadata.email_for_model_steward || '',
                }
              : null,
          )
          .filter((steward): steward is ModelStewardInfo => !!steward)
          .reduce((map, steward) => {
            map[buildStewardKey(steward)] = steward;
            return map;
          }, {} as any as {[key: string]: ModelStewardInfo}),
      ).sort(compareStewards),
      creators: Object.values(
        (models || [])
          .filter((model): model is ModelDTO => !!model.createdBy.id)
          .reduce((map, model) => {
            map[buildCreatorKey(model.createdBy)] = model.createdBy;
            return map;
          }, {} as any as {[key: string]: ModelCreator}),
      ).sort(compareCreators),
      products: extractDistinctMetadata(models, 'product'),
      diseases: extractDistinctMetadata(models, 'disease'),
      type: extractDistinctMetadata(models, 'type'),
      therapeuticAreas: extractDistinctMetadata(models, 'therapeutic_area'),
      platforms: extractDistinctMetadata(models, 'platform'),
      status: ['Draft', 'Published', 'Retired'],
    };
  }, [models]);

  const [expandedFilter, setExpandedFilter] = useState<string | null>(null);

  const form = useForm(
    new FormGroup({
      modelTypes: new FormControl([]),
      global: new FormControl([]),
      countries: new FormControl([]),
      stewards: new FormControl([]),
      creators: new FormControl([]),
      products: new FormControl([]),
      diseases: new FormControl([]),
      type: new FormControl([]),
      therapeuticAreas: new FormControl([]),
      platforms: new FormControl([]),
      status: new FormControl(['Draft', 'Published']),
    }),
  );

  useEffect(() => {
    if (!!models && !expandedFilter) {
      const populatedFilterKeys = Object.entries(filter || {})
        .filter(([, filterValue]) => filterValue && filterValue.length > 0)
        .map(([filterKey]) => filterKey);

      setExpandedFilter(
        (Object.keys(options) as (keyof typeof options)[]).find(
          (optionsKey) =>
            !!options[optionsKey] &&
            !!options[optionsKey].length &&
            (populatedFilterKeys.length === 0 || populatedFilterKeys.includes(optionsKey)),
        ) || null,
      );
    }
  }, [models, filter, expandedFilter, setExpandedFilter, options]);

  // Counts total items in all arrays of an object
  useEffect(() => {
    // Update form value when filter changes
    if (filter) {
      form.setValue(filter, {emitEvent: false});
    }
  }, [form, filter]);

  useEffect(() => {
    // Subscribe to value changes once on mount
    form.valueChanges.subscribe(onFilterChange);
    return () => form.valueChanges.unsubscribe(onFilterChange);
  }, [form, onFilterChange]);

  const checkboxFilterProps = <G extends keyof typeof options>(group: G) => ({
    isHorizontal,
    control: form.get(group),
    collapsed: expandedFilter !== group,
    onToggleCollapsed: () => setExpandedFilter(expandedFilter === group ? null : group),
    options: group !== 'stewards' && group !== 'creators' ? mapOptions(options[group]) : options[group],
  });

  return (
    <div>
      {models ? (
        <Form
          group={form}
          FieldGroupProps={{strict: false}}
          isHorizontal={isHorizontal}
          render={() => (
            <>
              <MultipleCheckboxFilter label="Model Types" {...checkboxFilterProps('modelTypes')} order={3} />

              {!isHorizontal && (
                <MultipleCheckboxFilter
                  label="HEDS Lead"
                  getDataTestIdPostfixFromValue={(s) => s.email || s.name || ''}
                  {...checkboxFilterProps('stewards')}
                  options={options.stewards?.map(({name, email}) => ({
                    name,
                    email,
                    label: `${name} (${email})`,
                    value: `${name} (${email})`,
                  }))}
                />
              )}

              {!isHorizontal && (
                <MultipleCheckboxFilter
                  label="Creator"
                  getDataTestIdPostfixFromValue={buildCreatorKey}
                  {...checkboxFilterProps('creators')}
                  options={options.creators?.map(({id, firstName, lastName, email}) => ({
                    id,
                    firstName,
                    lastName,
                    email,
                    label: firstName && lastName && email ? `${firstName} ${lastName} (${email})` : id,
                    value: `${id}`,
                  }))}
                />
              )}

              {!isHorizontal && (
                <MultipleCheckboxFilter
                  label={!!filter?.global?.length ? 'Global / Country adapt.' : 'Global / Country adaptation'}
                  {...checkboxFilterProps('global')}
                  options={options.global.map((option) => ({
                    label: option === 'global' ? 'Global' : 'Country Adaptation',
                    value: option,
                  }))}
                />
              )}
              {!isHorizontal && <MultipleCheckboxFilter label="Countries" {...checkboxFilterProps('countries')} />}

              <MultipleCheckboxFilter label="Product" {...checkboxFilterProps('products')} order={4} />
              <MultipleCheckboxFilter
                label="Platform"
                {...checkboxFilterProps('platforms')}
                options={options.platforms.map((option) => ({
                  label: option === 'Pneumococcal Vaccines' ? 'PneuInsights' : option,
                  value: option,
                }))}
                order={5}
              />

              <MultipleCheckboxFilter label="Name of Disease" {...checkboxFilterProps('diseases')} order={2} />

              {!isHorizontal && (
                <MultipleCheckboxFilter
                  label="Model Language"
                  {...checkboxFilterProps('type')}
                  options={options.type.map((option) => ({
                    label: option === 'ExcelNative' ? 'Excel' : option || '',
                    value: option,
                  }))}
                />
              )}

              <MultipleCheckboxFilter
                label="Therapeutic Area "
                {...checkboxFilterProps('therapeuticAreas')}
                order={1}
              />
              {!isHorizontal && <MultipleCheckboxFilter label="Status" {...checkboxFilterProps('status')} />}
            </>
          )}
        />
      ) : (
        <Spin size="large" style={{display: 'block', margin: '40px auto'}} />
      )}
    </div>
  );
};
